import PropTypes from 'prop-types'
import React, { useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import Helmet from 'react-helmet'
import { isEmpty } from 'lodash'
import { artistsStatus } from 'reduxModules/artists'
import {
  artistStateSelector as stateSelector
} from 'reduxModules/artists/selectors'
import {
  artistDispatchers as dispatchers
} from 'reduxModules/artists/dispatchers'

import {
  TutorialSummaryPropTypes,
  WebinarSummaryPropTypes
} from 'components/generic/CourseSummary/propTypes'
import BlogPostPropTypes from 'components/Blog/BlogPosts/BlogPost/propTypes'
import AssetGalleryPropTypes from 'components/generic/AssetsGallery/propTypes'

import Tutorial from '../../generic/CourseSummary'
import BlogPosts from '../../Blog/BlogPosts'
import AssetsGallery from '../../generic/AssetsGallery/_lazy'
import FavoriteButton from '../../generic/FavoriteButton'

import './styles.css'

const SUPPORTED_NETWORKS = [
  'facebook',
  'twitter',
  'instagram',
  'youtube',
  'pinterest',
  'vimeo',
  'linkedin',
  'behance',
  'deviantart',
  'flickr',
  'tumblr',
  'google',
  'imdb'
]

const BANNER_IMAGE = '../../../global/assets/images/artists/teachers-bio-bg.jpg'

function getIconName(network) {
  const target = network.toLowerCase().trim()
  if (SUPPORTED_NETWORKS.includes(target)) return network
  return 'website'
}

function getSimplifiedFirstName(firstName) {
  if (!firstName) return ''
  if (firstName.toLowerCase() === 'chiodo') return 'chiodo bros'
  return firstName.split(' ', 2)[0]
}

export function ArtistDetail(props) {
  const {
    loadArtistInfo,
    loadArtistTutorials,
    loadArtistWebinars,
    clearArtist,
    triggerPendingScroll,
    router,
    route,
    status,
    slug,
    url,
    intro,
    photoThumbnail,
    title,
    testimonialBody,
    testimonialBy,
    testimonialByDescription,
    bio,
    firstName,
    lastName,
    networks,
    tutorials,
    webinars,
    blogPosts,
    assets,
    favorited,
    facebookShareImages,
    metaTitle,
    metaDescription
  } = props

  const routerWillLeave = useCallback((nextLocation) => {
    if (nextLocation.pathname === '/artists') triggerPendingScroll()
    return true
  }, [triggerPendingScroll])

  useEffect(() => {
    loadArtistInfo()
    loadArtistTutorials()
    loadArtistWebinars()

    if (router?.setRouteLeaveHook) {
      router.setRouteLeaveHook(route, routerWillLeave)
    }

    return () => clearArtist()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (status !== artistsStatus.success) return null

  const displayMetaTitle = metaTitle || `${firstName} ${lastName} biography`
  const displayMetaDescription = metaDescription || intro
  const simplifiedFirstName = getSimplifiedFirstName(firstName)

  return (
    <div id="ArtistDetail">
      <Helmet>
        <title>{displayMetaTitle} | Stan Winston School of Character Arts</title>
        <meta name="description" content={displayMetaDescription} />
        <meta property="og:title" content={displayMetaTitle} />
        <meta property="og:description" content={displayMetaDescription} />
        {!facebookShareImages.length && (
          <meta property="og:image" content={photoThumbnail.original} />
        )}
        {facebookShareImages.length > 0 && facebookShareImages.map((fbImage, index) => (
          <meta
            key={index}
            property="og:image"
            content={fbImage.original}
          />
        ))}
        <meta property="og:type" content="profile" />
      </Helmet>

      {firstName && lastName && (
        <section id="header">
          <img src={BANNER_IMAGE} alt={`${firstName} ${lastName}`} />
        </section>
      )}

      <div className="wrapper main-about-holder">
        <section id="main" className={!BANNER_IMAGE ? '-push' : ''}>
          {firstName && lastName && (
            <div className="photo">
              <img
                src={photoThumbnail.medium}
                role="presentation"
                alt={`${firstName} ${lastName}`}
              />
            </div>
          )}

          <h1>{title}</h1>

          {intro && <div className="intro">{intro}</div>}

          {!isEmpty(networks) && (
            <div className="social">
              <h3>Links</h3>
              {networks.map((network, index) => {
                const iconName = getIconName(network.name)
                return (
                  <a
                    key={index}
                    className={iconName}
                    title={network.name}
                    href={network.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className={`icon icon-${iconName}`} />
                    <span>{network.name}</span>
                  </a>
                )
              })}
            </div>
          )}

          {testimonialBody && <hr />}

          {testimonialBody && (
            <div className="testimonial">
              <div className="body quoted">{testimonialBody}</div>
              <div className="by">{testimonialBy}</div>
              {testimonialByDescription && (
                <div className="byDescription">{testimonialByDescription}</div>
              )}
            </div>
          )}
        </section>

        <section id="about" className={`wrapper ${!BANNER_IMAGE ? '-push' : ''}`}>
          <FavoriteButton favorited={favorited} url={url} />
          <div className="bio">
            <h2>About {simplifiedFirstName}</h2>
            {bio && (
              <div
                className="bio-content formatted"
                dangerouslySetInnerHTML={{ __html: bio }}
              />
            )}
          </div>
        </section>
      </div>

      {assets && assets.length > 0 && <AssetsGallery assets={assets} />}

      <div className="wrapper">
        {tutorials.length > 0 && (
          <section id="courses">
            <h2 className="title-division">Courses by {title}</h2>
            <div className="tutorials-list-content">
              {webinars.map(webinar => (
                <Tutorial
                  key={`webinar-${webinar.id}`}
                  className="tutorial"
                  slug={webinar.slug}
                  summary={webinar.tagline}
                  teacher={webinar.artist.title}
                  teacherSlug={webinar.artist.slug}
                  title={webinar.title}
                  thumb={webinar.listingPhoto.boxThumb}
                  isWebinar
                />
              ))}
              {tutorials.map(tutorial => (
                <Tutorial
                  key={`tutorial-${tutorial.id}`}
                  className="tutorial"
                  slug={tutorial.slug}
                  summary={tutorial.description}
                  teacher={tutorial.artist.title}
                  teacherSlug={tutorial.artist.slug}
                  title={tutorial.title}
                  thumb={tutorial.photo.boxThumb}
                  new={tutorial.new}
                />
              ))}
            </div>
          </section>
        )}

        {blogPosts.length > 0 && (
          <section id="posts">
            <h2 className="title-division">Related Posts</h2>
            <BlogPosts blogPosts={blogPosts} />
          </section>
        )}
      </div>
    </div>
  )
}

ArtistDetail.propTypes = {
  loadArtistInfo: PropTypes.func.isRequired,
  loadArtistTutorials: PropTypes.func.isRequired,
  loadArtistWebinars: PropTypes.func.isRequired,
  clearArtist: PropTypes.func.isRequired,
  triggerPendingScroll: PropTypes.func,
  router: PropTypes.object,
  route: PropTypes.object,
  status: PropTypes.string.isRequired,

  slug: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  intro: PropTypes.string,
  photoThumbnail: PropTypes.objectOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  testimonialBody: PropTypes.string,
  testimonialBy: PropTypes.string,
  testimonialByDescription: PropTypes.string,
  bio: PropTypes.string,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,

  networks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ).isRequired,

  tutorials: PropTypes
    .arrayOf(PropTypes.shape(TutorialSummaryPropTypes))
    .isRequired,
  webinars: PropTypes
    .arrayOf(PropTypes.shape(WebinarSummaryPropTypes))
    .isRequired,
  blogPosts: PropTypes.arrayOf(PropTypes.shape(BlogPostPropTypes)).isRequired,
  assets: PropTypes.arrayOf(PropTypes.shape(AssetGalleryPropTypes)).isRequired,
  favorited: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  facebookShareImages: PropTypes.arrayOf(PropTypes.shape({
    original: PropTypes.string
  }))
}

ArtistDetail.defaultProps = {
  slug: '',
  title: '',
  url: '',
  firstName: '',
  lastName: '',
  photoThumbnail: {
    medium: ''
  },
  tutorials: [],
  webinars: [],
  blogPosts: [],
  assets: [],
  facebookShareImages: [],
  favorited: null,
  triggerPendingScroll: () => {},
  router: null,
  route: null,
  metaTitle: '',
  metaDescription: ''
}

export default connect(
  stateSelector,
  dispatchers
)(withRouter(ArtistDetail))
